import { Button, Col, Divider, Flex, Form, Input, Row, Select, Tooltip } from "antd";
import { useEffect, useRef } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";
import { CadastrarUsuarioModel } from "../../../../models/account/chamadas/cadastrarUsuarioModel";
import { atualizarUsuario, cadastrarUsuario, listarRole } from "../../../../services/accountService";
import { AtualizarUsuarioModel } from "../../../../models/account/chamadas/atualizarUsuarioModel";
import { setModalCadastrarUsuario } from "../../../../redux/slices/account/accountSlice";
import { buscarEmpresa } from "../../../../services/empresaService";
import { EmpresasUsuarioModel } from "../../../../models/account/empresasUsuarioModel";

type FormProps = {
  email: string;
  password: string;
  nome: string;
  perfil: string[];
  idEmpresa: number[];
};

const CadastrarUsuarioModal = () => {
  //estados globais
  const empresaState = useAppSelector((state) => state.empresa);
  const accountState = useAppSelector((state) => state.account);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<FormProps>();

  const editar = Object.values(accountState.usuario).length !== 0;

  const inputInicial = useRef<any>(null);

  let watchPerfil = Form.useWatch("perfil", form);

  useEffect(() => {
    if (inputInicial.current && !editar) {
      inputInicial.current.focus();
    }
    dispatch(buscarEmpresa(empresaState.busca));
    dispatch(listarRole());

    if (editar) {
      form.setFieldsValue({
        ...accountState.usuario,
        perfil: accountState.usuario.perfis ? accountState.usuario.perfis : [],
        idEmpresa: accountState.usuario.empresas?.map((empresa) => empresa.idEmpresa),
      });
    }
  }, [accountState.usuario]);

  const submit = (values: FormProps) => {
    if (editar) {
      let args: AtualizarUsuarioModel = {
        ...values,
        idUsuario: accountState.usuario.id,
        perfilAnterior: accountState.usuario.perfis ? accountState.usuario.perfis : [],
        empresas: !values.perfil.some((p) => p.toUpperCase() === "ADMIN") ? getUsuarioEmpresas(values.idEmpresa) : null,
      };
      dispatch(atualizarUsuario({ value: args, busca: accountState.busca }));
    } else {
      let args: CadastrarUsuarioModel = {
        ...values,
        confirmPassword: values.password,
      };
      dispatch(cadastrarUsuario({ value: args, busca: accountState.busca }));
    }
  };

  const getUsuarioEmpresas = (idEmpresa: number[]): EmpresasUsuarioModel[] => {
    const empresasOriginais: EmpresasUsuarioModel[] = accountState.usuario.empresas || [];

    let empresas: EmpresasUsuarioModel[] = empresasOriginais.filter((item) => idEmpresa.includes(item.idEmpresa)).map((item) => ({ ...item }));

    idEmpresa.forEach((id) => {
      if (!empresas.some((item) => item.idEmpresa === id)) {
        empresas.push({ idEmpresa: id, permissao: true, logoGrande: "", logoPequeno: "", empresa: "" });
      }
    });

    return empresas;
  };

  const onChangePerfil = (value: string[]) => {
    if (value.some((v) => v.toUpperCase() === "ADMIN")) {
      form.resetFields(["idEmpresa"]);
    }
  };

  const voltar = () => {
    dispatch(setModalCadastrarUsuario(false));
  };

  return (
    <Form form={form} layout="vertical" onFinish={submit}>
      <Row gutter={12}>
        <Col span={24}>
          <Flex gap={16}>
            <Form.Item
              label="Endereço de e-mail"
              name="email"
              rules={[
                {
                  required: true,
                  message: "Digite seu email",
                },
              ]}
            >
              <Input type="text" placeholder="Digite seu e-mail" ref={inputInicial} style={{ width: 300 }} />
            </Form.Item>
            {!editar && (
              <Form.Item
                label="Digite sua senha"
                name="password"
                rules={[
                  {
                    required: true,
                    message: "Digite sua senha",
                  },
                ]}
              >
                <Input type="password" placeholder="Digite sua senha" style={{ width: 300 }} />
              </Form.Item>
            )}
            <Form.Item
              label="Nome"
              name="nome"
              rules={[
                {
                  required: true,
                  message: "Digite seu nome",
                },
              ]}
            >
              <Input type="text" placeholder="Digite seu nome" style={{ width: 200 }} />
            </Form.Item>
          </Flex>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            label="Selecione o perfil"
            name="perfil"
            rules={[
              {
                required: true,
                message: "Selecione pelo menos um perfil",
              },
            ]}
          >
            <Select
              showSearch
              mode="multiple"
              onChange={onChangePerfil}
              optionFilterProp="label"
              placeholder="Selecione o perfil"
              loading={accountState.loadingRoles}
              disabled={accountState.usuario.perfis?.includes("CONSIGNATARIO")}
              options={accountState.roles.map((a: any) => ({
                value: a.name,
                label: a.normalizedName,
                disabled: a.normalizedName === "CONSIGNATARIO",
              }))}
            />
          </Form.Item>
        </Col>
        {watchPerfil !== undefined && !watchPerfil.some((perfil: string) => perfil.toUpperCase() === "ADMIN") && (
          <Col span={12}>
            <Form.Item
              label="Selecione a empresa"
              name="idEmpresa"
              rules={[
                {
                  required: true,
                  message: "Selecione pelo menos uma empresa",
                },
              ]}
            >
              <Select
                showSearch
                mode="multiple"
                maxTagCount="responsive"
                maxTagPlaceholder={(omittedValues) => (
                  <Tooltip
                    title={
                      <ul style={{ paddingLeft: 16, margin: 0 }}>
                        {omittedValues.map((field, index) => (
                          <li key={index}>{field.label}</li>
                        ))}
                      </ul>
                    }
                  >
                    <span>{`+ ${omittedValues.length} ...`}</span>
                  </Tooltip>
                )}
                optionFilterProp="label"
                placeholder="Selecione a empresa"
                disabled={accountState.usuario.perfis?.includes("CONSIGNATARIO")}
                loading={empresaState.loading}
                options={empresaState.list.map((a: any) => ({
                  value: a.id,
                  label: a.apelido,
                }))}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Divider />
      <Flex align="center" justify="flex-end" gap={10}>
        <Button className="cancelButton" onClick={voltar} icon={<CloseOutlined />}>
          FECHAR
        </Button>
        <Button htmlType="submit" icon={<CheckOutlined />} className="buttonPrimary">
          {editar ? "ATUALIZAR" : "SALVAR"}
        </Button>
      </Flex>
    </Form>
  );
};

export default CadastrarUsuarioModal;
